import { Routes, Route, Navigate } from 'react-router-dom';
import { SignIn, SignUp, useUser, useAuth } from '@clerk/clerk-react';
import { useEffect, useCallback } from 'react';
import { Toaster } from 'react-hot-toast';
import { setAuthTokenGetter } from './utils/api';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import DocsPage from './pages/DocsPage';
import PricingPage from './pages/PricingPage';
import CheckoutPage from './pages/CheckoutPage';
import DashboardLayout from './pages/DashboardLayout';
import DashboardOverview from './pages/DashboardOverview';
import ApiKeysPage from './pages/ApiKeysPage';
import ApiPlayground from './pages/ApiPlayground';
import AnalyticsLogsPage from './pages/AnalyticsLogsPage';
import SettingsPage from './pages/SettingsPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isSignedIn, isLoaded } = useUser();

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  return children;
};

// Auth Layout Component
const AuthLayout = ({ children }) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-950 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {children}
      </div>
    </div>
  );
};

function App() {
  const { getToken, isLoaded } = useAuth();

  // Memoize the token getter to prevent unnecessary re-renders
  const stableGetToken = useCallback(async () => {
    if (!isLoaded) {
      // Wait for auth to be loaded
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    return getToken();
  }, [getToken, isLoaded]);

  // Initialize the auth token getter when the app loads
  useEffect(() => {
    setAuthTokenGetter(stableGetToken);
  }, [stableGetToken]);

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: 'var(--toast-bg)',
            color: 'var(--toast-color)',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />

      <Routes>
        {/* Public Routes */}
        <Route
          path="/"
          element={
            <>
              <Navbar />
              <HomePage />
              <Footer />
            </>
          }
        />

        {/* Docs Route - Public */}
        <Route
          path="/docs"
          element={
            <>
              <Navbar />
              <DocsPage />
            </>
          }
        />

        {/* Pricing Route - Public */}
        <Route
          path="/pricing"
          element={
            <>
              <Navbar />
              <PricingPage />
              <Footer />
            </>
          }
        />

        {/* Checkout Route - Protected */}
        <Route
          path="/checkout"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <CheckoutPage />
                <Footer />
              </>
            </ProtectedRoute>
          }
        />

        {/* Auth Routes */}
        <Route
          path="/sign-in/*"
          element={
            <AuthLayout>
              <SignIn
                routing="path"
                path="/sign-in"
                signUpUrl="/sign-up"
                afterSignInUrl="/dashboard"
              />
            </AuthLayout>
          }
        />
        <Route
          path="/sign-up/*"
          element={
            <AuthLayout>
              <SignUp
                routing="path"
                path="/sign-up"
                signInUrl="/sign-in"
                afterSignUpUrl="/dashboard"
              />
            </AuthLayout>
          }
        />

        {/* Protected Dashboard Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <DashboardLayout />
              </>
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardOverview />} />
          <Route path="keys" element={<ApiKeysPage />} />
          <Route path="playground" element={<ApiPlayground />} />
          <Route path="analytics" element={<AnalyticsLogsPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* Catch all - 404 */}
        <Route
          path="*"
          element={
            <>
              <Navbar />
              <div className="min-h-screen flex items-center justify-center pt-16">
                <div className="text-center">
                  <h1 className="text-6xl font-bold gradient-text mb-4">404</h1>
                  <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
                    Page not found
                  </p>
                  <a href="/" className="btn-primary">
                    Go Home
                  </a>
                </div>
              </div>
              <Footer />
            </>
          }
        />
      </Routes>
    </>
  );
}

export default App;
